<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import Fuse from 'fuse.js'
import { vMaska } from 'maska'
import textInput from '@/components/common/textInput.vue'
import BSelect from '@/components/common/bSelect.vue'
import bAutocomplete from '@/components/common/bAutocomplete.vue'
import countryPhoneMask from '@/assets/app/countryPhoneMask.json'
import CountryFlag from '@/components/common/CountryFlag.vue'
import CarbonDocumentUnknown from '~icons/carbon/document-unknown'

import { size as sizeConst } from '@/services/application/constantes'

const props = withDefaults(defineProps<{
  defaultCountry?: string
  label?: string
  disabled?: boolean
  required?: boolean
  size?: keyof typeof sizeConst
  autocomplete?: string
  name?: string
  id?: string
}>(), {
  defaultCountry: 'fr',
  required: false,
  size: 'small',
  autocomplete: 'tel',
  name: 'phone',
  id: undefined,
})

const inputValue = defineModel<string>()

const newInputValue = ref(inputValue.value)

const options = ref(countryPhoneMask)
const phonemask = ref()

const localSearchProjectOptions = {
  includeScore: true,
  keys: [
    { name: 'display', getFn: country => country.display },

  ],
  threshold: 0.3,

}

const search = ref('')

const searchList = computed(() => {
  if (!search.value)
    return options.value
  const fuse = new Fuse(options.value, localSearchProjectOptions)
  return fuse.search(search.value).map(item => item.item)
})

const findPhoneMaskFromNumber = (number: string) => {
  const country = countryPhoneMask.find(item => number.replace('+', '').startsWith(item.dialCode))
  if (!country)
    return
  return country
}

const setDefaultCountry = async () => {
  const country = findPhoneMaskFromNumber(newInputValue.value || '')
  phonemask.value = country
  if (country)
    return
  const countryFromIso = countryPhoneMask.find(item => item.countryId === props.defaultCountry.toLowerCase())
  if (!countryFromIso)
    return

  // Whitout this, the input value is not updated idk why :) by Mathieu Janio <3 vue 3.4.0
  await nextTick()
  newInputValue.value = `+${countryFromIso.dialCode}${(newInputValue.value || '')}`
  phonemask.value = countryFromIso
}

watch(() => props.defaultCountry, () => setDefaultCountry())

watch(inputValue, (value: string) => {
  const startWithPlus = (value || '').startsWith('+') || newInputValue.value.startsWith('+')
  if (!startWithPlus) {
    newInputValue.value = `+${value}`
  } else if (value && value !== newInputValue.value) {
    // Sync newInputValue when inputValue changes externally (e.g., from AI scan)
    newInputValue.value = value
  }

  const isCurrentCountry = phonemask.value && (value || '').startsWith(`+${phonemask.value.dialCode}`)
  if (isCurrentCountry)
    return
  const country = findPhoneMaskFromNumber(value || newInputValue.value || '')
  if (!country)
    return phonemask.value = undefined

  phonemask.value = country
})

const isOpen = ref(false)

const selectNumber = (hide, item) => {
  phonemask.value = item
  const country = findPhoneMaskFromNumber(newInputValue.value || '')
  if (!country)
    newInputValue.value = `+${item.dialCode}${(newInputValue.value || '').replace('+', '')}`

  else
    newInputValue.value = `+${item.dialCode}${(newInputValue.value || '').replace('+', '').replace(country.dialCode, '')}`

  if (hide)
    hide()
}

const checkInput = () => {
  if (newInputValue.value === `+${phonemask.value?.dialCode}`)
    inputValue.value = ''
  else
    inputValue.value = newInputValue.value
}

setDefaultCountry()
</script>

<template>
  <VDropdown :shown="isOpen" :distance="6" placement="bottom" class="self-start" :triggers="[]" @update:shown="isOpen"  :class="{'mt-5': label}">
    <template #default="{ show, hide }">
      <text-input
        v-model="newInputValue" shadow="shadow-none" input-container-class="rounded-l-none" :disabled="disabled"
        :autocomplete="autocomplete" :name="name" :id="id" @input="checkInput()"
        :size="size"
      >
        <template #prepend>
          <div
            :class="[{ 'cursor-pointer': !disabled }, sizeConst[size], { 'cursor-not-allowed': disabled }]"
            class="flex items-center bg-base z-1 border border-base rounded-l-xl p-2 gap-1 w-36 relative"
            @click="!disabled ? isOpen ? hide() : show() : hide()"
          >
            <p class="subtitle-field line-clamp-1 absolute -top-3 left-0">
              {{ label }} <span v-if="required" class="text-sm text-danger-400">*</span>
            </p>
            <template v-if="phonemask">
              <CountryFlag :country="phonemask.iso" />
              <span class="ml-2 line-clamp-1">{{ phonemask.display }}</span>
            </template>
            <template v-else>
              <CarbonDocumentUnknown class="text-danger-400" />
              unknown
            </template>
          </div>
        </template>
      </text-input>
    </template>

    <template #popper="{ hide }">
      <div class="bg-base w-64 p-4 rounded-xl shadow-sm flex flex-col gap-2">
        <text-input v-model="search" container-class="w-full" placeholder="FR +33" />

        <ul v-auto-animate class="max-h-[300px] overflow-y-auto overflow-x-hidden pr-2">
          <li
            v-for="item in searchList" :key="item._id"
            class="flex gap-2 px-2 py-1 hover:bg-fade rounded-lg cursor-pointer items-center"
            @click="selectNumber(hide, item)"
          >
            <CountryFlag :country="item.iso" />
            {{ item.display }}
          </li>
        </ul>
      </div>
    </template>
  </VDropdown>
</template>
